/**
 * Role-Based Access Control Components
 *
 * SECURITY NOTE: These components provide frontend route protection for better UX.
 * The backend API MUST also enforce role-based access control.
 * Frontend guards alone are NOT sufficient for security.
 *
 * Usage:
 *   <AdminRequired><AdminDashboard /></AdminRequired>
 *   <AuditorRequired><AuditorDashboard /></AuditorRequired>
 *   <RegionalDirectorRequired><RegionalDirectorDashboard /></RegionalDirectorRequired>
 *   <GlobalDirectorRequired><GlobalDirectorDashboard /></GlobalDirectorRequired>
 *   <DirectorRequired><DirectorPage /></DirectorRequired>
 *   <RoleRequired roles={['admin', 'auditor']}><SharedPage /></RoleRequired>
 */
import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../authProvider.mjs';
import PageShell from './PageShell.mjs';

/**
 * Generic role-based route guard
 * @param {Object} props
 * @param {string[]} props.roles - Array of allowed roles (e.g., ['admin', 'auditor'])
 * @param {React.ReactNode} props.children - Child components to render if authorized
 * @param {string} props.redirectTo - Where to redirect unauthorized users (default: '/dashboard')
 */
export const RoleRequired = ({ roles = [], children, redirectTo = '/dashboard' }) => {
    const {
        isAuthenticated,
        isLoading,
        isAdmin,
        isAuditor,
        isRegionalDirector,
        isGlobalDirector,
        isFinanceDirector,
        user
    } = useContext(AuthContext);
    const navigate = useNavigate();

    // Still loading if authenticated but user data not yet populated
    const stillLoading = isLoading || (isAuthenticated && !user);

    // Compute role check directly to avoid stale closure issues.
    // SECURITY: Only use server-verified boolean flags from AuthContext - never
    // read user.role directly, as that field is mutable state that could be set
    // without going through the server-verified updateUserStates path.
    // SECURITY: Empty roles array denies access (fail-closed).
    const hasRequiredRole = (() => {
        if (!roles || roles.length === 0) return false;

        if (roles.includes('admin') && isAdmin) return true;
        if (roles.includes('auditor') && isAuditor) return true;
        if (roles.includes('regional_director') && isRegionalDirector) return true;
        if (roles.includes('global_director') && isGlobalDirector) return true;
        if (roles.includes('finance_director') && isFinanceDirector) return true;

        return false;
    })();

    useEffect(() => {
        // Only redirect after loading is complete, user is populated, and we know the user doesn't have the role
        if (!stillLoading && isAuthenticated && !hasRequiredRole) {
            navigate(redirectTo, { replace: true });
        }
    }, [stillLoading, isAuthenticated, hasRequiredRole, navigate, redirectTo]);

    // Both states render in the same page frame as the page they guard, so the
    // band is already there while permissions resolve (no gray flash first).
    if (stillLoading) {
        return (
            <PageShell>
                <div className="bg-white rounded-xl shadow-lg py-16 flex flex-col items-center justify-center">
                    <div className="w-10 h-10 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin mb-4"></div>
                    <p className="text-gray-600">Checking permissions...</p>
                </div>
            </PageShell>
        );
    }

    if (!isAuthenticated) {
        return null; // AuthRequired will handle the redirect
    }

    if (!hasRequiredRole) {
        return (
            <PageShell title="Access Denied">
                <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 max-w-md mx-auto text-center" role="alert">
                    <svg className="w-10 h-10 mx-auto mb-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
                    <p className="text-gray-600">You do not have permission to view this page.</p>
                </div>
            </PageShell>
        );
    }

    return children;
};

/**
 * Admin-only route guard
 */
export const AdminRequired = ({ children, redirectTo = '/dashboard' }) => {
    return (
        <RoleRequired roles={['admin']} redirectTo={redirectTo}>
            {children}
        </RoleRequired>
    );
};

/**
 * Auditor-only route guard
 */
export const AuditorRequired = ({ children, redirectTo = '/dashboard' }) => {
    const { isAuditorDelegate } = useContext(AuthContext);
    // Someone covering for an auditor is admitted on the same footing. The
    // server decides WHICH regions they see; this only opens the page.
    return (
        <RoleRequired
            roles={isAuditorDelegate ? ['auditor', 'admin', 'regional_director', 'global_director', 'finance_director'] : ['auditor']}
            redirectTo={redirectTo}
        >
            {children}
        </RoleRequired>
    );
};

/**
 * Report-review staff: every role that can review monthly reports OR hold an
 * auditor's delegation.
 *
 * For the auditor dashboard. Narrower than AuthRequired - a claimant can never
 * be an auditor or a delegate, so they are refused outright - but wider than
 * AuditorRequired, because an auditor's stand-in is a staff member whose own
 * role is not 'auditor', and the server already admits them to the dashboard
 * data for the regions they cover. The server remains the authority on WHICH
 * reports they see; this only keeps the page from rendering for someone who can
 * never be entitled to it.
 */
export const ReportReviewStaffRequired = ({ children, redirectTo = '/dashboard' }) => {
    return (
        <RoleRequired
            roles={['auditor', 'admin', 'regional_director', 'global_director', 'finance_director']}
            redirectTo={redirectTo}
        >
            {children}
        </RoleRequired>
    );
};

/**
 * Admin or Auditor route guard (for shared administrative pages)
 */
export const AdminOrAuditorRequired = ({ children, redirectTo = '/dashboard' }) => {
    return (
        <RoleRequired roles={['admin', 'auditor']} redirectTo={redirectTo}>
            {children}
        </RoleRequired>
    );
};

/**
 * Regional Director route guard
 */
export const RegionalDirectorRequired = ({ children, redirectTo = '/dashboard' }) => {
    return (
        <RoleRequired roles={['regional_director']} redirectTo={redirectTo}>
            {children}
        </RoleRequired>
    );
};

/**
 * Global Director route guard (also allows Finance Director)
 */
export const GlobalDirectorRequired = ({ children, redirectTo = '/dashboard' }) => {
    return (
        <RoleRequired roles={['global_director', 'finance_director']} redirectTo={redirectTo}>
            {children}
        </RoleRequired>
    );
};

/**
 * Finance Director route guard
 */
export const FinanceDirectorRequired = ({ children, redirectTo = '/dashboard' }) => {
    return (
        <RoleRequired roles={['finance_director']} redirectTo={redirectTo}>
            {children}
        </RoleRequired>
    );
};

/**
 * Any Director route guard (regional, global, or finance director)
 */
export const DirectorRequired = ({ children, redirectTo = '/dashboard' }) => {
    return (
        <RoleRequired roles={['regional_director', 'global_director', 'finance_director']} redirectTo={redirectTo}>
            {children}
        </RoleRequired>
    );
};

/**
 * Approver route guard (auditor or any director role that can approve reports)
 */
export const ApproverRequired = ({ children, redirectTo = '/dashboard' }) => {
    return (
        <RoleRequired roles={['auditor', 'regional_director', 'global_director', 'finance_director']} redirectTo={redirectTo}>
            {children}
        </RoleRequired>
    );
};

export default RoleRequired;
